import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { useApi } from 'hooks/SwrCustom';
import AfasService from 'Api/Afas';

const AfasContext = createContext(null);

export const AfasProvider = ({ children }) => {
    const [communityId, setCommunityId] = useState(null);
    const [selectedSubstitutionId, setSelectedSubstitutionId] = useState(null);
    const [historiePeriod, setHistoriePeriod] = useState('');

    // AFAS-configured orgs, already filtered by allowlist for the current coordinator.
    // This endpoint is the source of truth for the footer org select — do not intersect
    // with GET /communities (role/eSubs quirks can drop allowlisted AFAS orgs).
    const { data: afasCommunitiesResponse } = useSWR('afas-configured-communities', () =>
        AfasService.getConfiguredCommunities().then((res) => res.data),
    );

    const communities = useMemo(() => {
        const afasList = afasCommunitiesResponse?.data ?? [];
        if (!Array.isArray(afasList) || afasList.length === 0) {
            return [];
        }

        return afasList.map((row) => ({
            iCommunityId: row.iCommunityId,
            vCommunityName:
                row.vCommunityName || `Bestuur ${row.iCommunityId}`,
            environment: row.environment,
            werkgever_id: row.werkgever_id,
        }));
    }, [afasCommunitiesResponse]);

    useEffect(() => {
        if (communities.length === 0) {
            if (communityId != null) {
                setCommunityId(null);
            }
            return;
        }

        const stillValid = communities.some(
            (community) => Number(community.iCommunityId) === Number(communityId),
        );

        if (communityId == null || !stillValid) {
            setCommunityId(communities[0].iCommunityId);
        }
    }, [communities, communityId]);

    const { data: countsData, mutate: refreshTabCounts } = useApi(
        communityId ? `afas/tabs/counts?community_id=${communityId}` : null,
    );

    const tabCounts = countsData?.data ?? {};

    const value = useMemo(
        () => ({
            communityId,
            setCommunityId,
            communities,
            selectedSubstitutionId,
            setSelectedSubstitutionId,
            historiePeriod,
            setHistoriePeriod,
            tabCounts,
            refreshTabCounts,
            AfasService,
        }),
        [
            communityId,
            communities,
            selectedSubstitutionId,
            historiePeriod,
            tabCounts,
            refreshTabCounts,
        ],
    );

    return <AfasContext.Provider value={value}>{children}</AfasContext.Provider>;
};

export const useAfasContext = () => {
    const context = useContext(AfasContext);
    if (!context) {
        throw new Error('useAfasContext must be used within AfasProvider');
    }
    return context;
};

export default AfasContext;
